import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Pill from '../shared/Pill';
import SectionState from '../shared/SectionState';
import { reasonLabel } from '../shared/labels';
import CallTaskFields from './CallTaskFields';
import { Notice } from './styles';
import { dueLabel, isOverdue, resultNote, shiftsLabel } from './callTaskFormat';
import useCallTaskDialog from './useCallTaskDialog';

import Button from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModalV2';
import Modal from '@/components/Modal';
import { Body } from '@/components/Typography';
import styled from '@/styles';

const Content = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '18px 24px',
});

const Actions = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '10px',
  padding: '14px 24px',
  borderTop: '1px solid #EDEDED',
});

const Inline = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px 24px',
  alignItems: 'center',
});

const Script = styled('div', {
  backgroundColor: '#F6FBF6',
  border: '1px solid #CFE6D3',
  borderRadius: '6px',
  padding: '12px 14px',
  lineHeight: 1.5,
});

type Props = {
  taskId: number;
  myUserId: number | null;
  onClose: () => void;
  onChanged: () => void;
  onCompleted: (message: string) => void;
};

const CallTaskModal = ({
  taskId,
  myUserId,
  onClose,
  onChanged,
  onCompleted,
}: Props) => {
  const dialog = useCallTaskDialog({ taskId, onChanged, onCompleted });
  const { task, form, setField, busy } = dialog;
  const navigate = useNavigate();
  // Where Discard goes: closing the window, or a page the admin clicked
  // ("Open profile"), which must ask first too (review RV-10).
  const [leaving, setLeaving] = useState<null | { to?: string }>(null);
  const unsaved = dialog.hasChanges || form.outcome !== (task?.outcome ?? '');
  const requestClose = () => {
    if (unsaved) setLeaving({});
    else onClose();
  };

  const renderBody = () => {
    if (dialog.load === 'loading') {
      return <SectionState mode="loading" title="the call task" />;
    }
    if (dialog.load === 'notFound') {
      return (
        <SectionState
          mode="empty"
          title="Not found"
          caption="This call task does not exist or is not in your tenant."
        />
      );
    }
    if (dialog.load === 'error' || !task) {
      return (
        <SectionState
          mode="error"
          error={dialog.loadError ?? undefined}
          onRetry={dialog.retryLoad}
        />
      );
    }

    const isFinal = task.status !== 'open';
    const dncReason = task.do_not_call_reason;
    // A worker can have no phone; copying it would write "null" (review C-04).
    const phone = task.worker.phone_number;
    const copyPhone = () => {
      if (phone) navigator.clipboard?.writeText(phone);
    };

    return (
      <>
        <Content>
          {isFinal && <Notice tone="grey">{resultNote(task)}</Notice>}
          {dncReason && (
            <Notice tone="red">
              {`Do not call · ${reasonLabel(dncReason).label}. You can still save notes.`}
            </Notice>
          )}
          <Inline>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <strong style={{ fontSize: 16 }}>
                {phone ?? 'No phone number'}
              </strong>
              <Button
                a11yLabel="Copy phone number"
                appearance="outline"
                disabled={!phone}
                size="sm"
                label="Copy"
                onClick={copyPhone}
              />
            </span>
            <span>
              {`${task.worker.city ?? '-'} · ${shiftsLabel(task.worker.open_shifts_nearby)}`}
            </span>
            <Link
              to={`/workers_v2/${task.worker.id}`}
              style={{ fontWeight: 600 }}
              onClick={(event) => {
                // A new tab (Cmd/Ctrl/Shift or middle click) keeps this window
                // and its notes, so it needs no discard question.
                const newTab =
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.button !== 0;
                if (!unsaved || newTab) return;
                event.preventDefault();
                setLeaving({ to: `/workers_v2/${task.worker.id}` });
              }}
            >
              Open profile
            </Link>
            <span
              style={
                isOverdue(task)
                  ? { color: '#B0030A', fontWeight: 700, marginLeft: 'auto' }
                  : { marginLeft: 'auto' }
              }
            >
              {task.status === 'open' ? dueLabel(task) : ''}
            </span>
          </Inline>
          <div>
            <Body weight="medium">Script</Body>
            <Script data-testid="callTaskScript">{task.script}</Script>
          </div>
          <CallTaskFields
            form={form}
            setField={setField}
            assignees={dialog.assignees}
            assigneesError={dialog.assigneesError}
            myUserId={myUserId}
            isFinal={isFinal}
            assignedId={task.assigned_to_user_id}
            assignedName={task.assigned_to_name}
            busy={busy}
          />
          {dialog.error && (
            <div role="alert" data-testid="callTaskError">
              <Notice tone="red">
                {dialog.error}
                {dialog.conflict && (
                  <>
                    {' '}
                    <Button
                      a11yLabel="Reload"
                      appearance="outline"
                      size="sm"
                      label="Reload"
                      onClick={dialog.reload}
                    />
                  </>
                )}
              </Notice>
            </div>
          )}
          {dialog.saved && (
            <div role="status">
              <Notice tone="green">
                {isFinal ? 'Saved.' : 'Saved. The task stays open.'}
              </Notice>
            </div>
          )}
        </Content>
        <Actions>
          <Button
            a11yLabel="Close"
            appearance="outline"
            label="Close"
            onClick={requestClose}
          />
          <Button
            a11yLabel="Save"
            appearance="outline"
            data-testid="callTaskSaveBtn"
            label="Save"
            disabled={busy || !dialog.hasChanges || Boolean(dialog.formError)}
            onClick={dialog.save}
          />
          <Button
            a11yLabel="Complete"
            appearance="primary"
            data-testid="callTaskCompleteBtn"
            label="Complete"
            title={dncReason ? reasonLabel(dncReason).label : undefined}
            disabled={
              isFinal ||
              busy ||
              !form.outcome ||
              Boolean(dncReason) ||
              Boolean(dialog.formError)
            }
            isLoading={busy}
            onClick={dialog.complete}
          />
        </Actions>
      </>
    );
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={task ? `Call ${task.worker.name}` : 'Call task'}
      onRequestClose={busy ? undefined : requestClose}
    >
      <div data-testid="callTaskModal">
        {task && (
          <Content css={{ paddingBottom: 0 }}>
            <Pill tone="grey">{`${task.journey_key} · ${task.step_label}`}</Pill>
          </Content>
        )}
        {renderBody()}
        {leaving && (
          <ConfirmationModal
            disableClickout
            cancelLabel="Keep editing"
            confirmLabel="Discard"
            message="Your call notes and choices are not saved."
            subMessage={null}
            title="Discard unsaved changes?"
            onCancel={() => setLeaving(null)}
            onConfirm={() => {
              const to = leaving.to;
              setLeaving(null);
              if (to) navigate(to);
              else onClose();
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default CallTaskModal;
